import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Radio, X, Smartphone } from "lucide-react";
import { PushSettings } from "@/components/PushSettings";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const Ctx = createContext({ items: [], unread: 0, banner: [] });
export const NotifCtx = Ctx;

export const NotificationsProvider = ({ children }) => {
  const { profile } = useProfile();
  const [d, setD] = useState({ items: [], unread: 0, banner: [] });
  const on = ["student", "parent", "teacher"].includes(profile?.role);
  const load = useCallback(() => http.get("/notifications").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => {
    if (!on) return setD({ items: [], unread: 0, banner: [] });
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [on, profile?.id, load]);
  const markRead = async () => { if (d.unread) { await http.post("/notifications/read"); setD((x) => ({ ...x, unread: 0, items: x.items.map((i) => ({ ...i, read: true })) })); } };
  return <Ctx.Provider value={{ ...d, markRead }}>{children}</Ctx.Provider>;
};

const when = (iso) => { const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); };

export const NotificationBell = () => {
  const { items, unread, markRead } = useContext(Ctx);
  const [push, setPush] = useState(false);
  const [menu, setMenu] = useState(false);
  return (
    <>
    <PushSettings open={push} setOpen={setPush} />
    <DropdownMenu open={menu} onOpenChange={(o) => { setMenu(o); if (o) markRead(); }}>
      <DropdownMenuTrigger data-testid="notif-bell" className="relative p-2 rounded-full text-[#2C4A3E] hover:bg-[#F1EADB] focus:outline-none" aria-label="Notifications">
        <Bell className="w-5 h-5" />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C0392B] text-white text-[10px] grid place-items-center" data-testid="notif-unread">{unread}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-[#FDFBF7] max-h-[420px] overflow-y-auto" data-testid="notif-list">
        <DropdownMenuLabel className="font-display text-[#2C4A3E] flex items-center justify-between">Reminders
          <button onClick={(e) => { e.preventDefault(); document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true })); setTimeout(() => setPush(true), 50); }} data-testid="push-settings-open" className="inline-flex items-center gap-1 text-xs font-sans text-[#C87D53] hover:underline"><Smartphone className="w-3.5 h-3.5" /> Phone alerts</button></DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((n) => (
          <Link key={n.id} to={n.link?.startsWith("http") ? "#" : n.link || "#"}
            onClick={(e) => { setMenu(false); if (n.link?.startsWith("http")) { e.preventDefault(); window.open(n.link, "_blank", "noopener"); } }}
            data-testid={`notif-item-${n.key}`} className={`block px-3 py-2 rounded-lg text-sm hover:bg-[#F1EADB] ${n.read ? "" : "bg-[#F7F3EB]"}`}>
            <b className="block text-[#2C4A3E]">{n.title}</b><span className="text-xs text-stone-500">{when(n.body)}</span>
          </Link>
        ))}
        {!items.length && <p className="px-3 py-4 text-sm text-stone-500" data-testid="notif-empty">No reminders yet.</p>}
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
};

export const ReminderBanner = () => {
  const { banner } = useContext(Ctx);
  const { profile } = useProfile();
  const [hidden, setHidden] = useState([]);
  const [all, setAll] = useState(false);
  const visible = banner.filter((b) => !b.school && !hidden.includes(`${b.class_id}:${b.kid_id}`));
  const show = all ? visible : visible.slice(0, 1);
  if (!visible.length) return null;
  const parent = profile?.role === "parent";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-4 space-y-2" data-testid="reminder-banner">
      {show.map((b) => (
        <div key={`${b.class_id}:${b.kid_id}`} className="rise rounded-2xl bg-[#2C4A3E] text-[#F7F3EB] px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`reminder-${b.class_id}`}>
          <Radio className={`w-5 h-5 ${b.status === "live" ? "text-[#F2D49B] animate-pulse" : ""}`} />
          <p className="flex-1 min-w-[200px] text-sm"><b>{parent ? `${b.kid_name}'s` : "Your"} {b.status === "live" ? "class is live now" : `live class starts in ${b.minutes} min`}:</b> {b.topic}</p>
          {parent ? (
            <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}${b.join_url}`); toast.success(`Join link copied for ${b.kid_name}`); }} data-testid={`reminder-copy-${b.class_id}`} className="px-4 py-2 rounded-full bg-[#F2D49B] text-[#5B3A1E] text-sm font-medium">Copy join link for {b.kid_name}</button>
          ) : (
            <Link to={b.join_url} data-testid={`reminder-join-${b.class_id}`} className="px-5 py-2 rounded-full bg-[#F2D49B] text-[#5B3A1E] text-sm font-medium hover:scale-105 transition-transform">Join now</Link>
          )}
          <button onClick={() => setHidden((h) => [...h, `${b.class_id}:${b.kid_id}`])} data-testid={`reminder-dismiss-${b.class_id}`} aria-label="Dismiss" className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      ))}
      {visible.length > 1 && <button onClick={() => setAll(!all)} data-testid="reminder-more" className="text-xs underline text-[#2C4A3E]">{all ? "Show less" : `+${visible.length - 1} more reminder${visible.length > 2 ? "s" : ""}`}</button>}
    </div>
  );
};
